// @flow
import {ROOT_PATH} from '../toURL';
import {FILTER} from './SearchUrlParameters';
import {encodeFilterValue} from '../parsers/filterRefinementUtils';
import type {Root} from '../toURL';

type BoundingBoxParameters = {
    neLat: number,
    neLong: number,
    swLat: number,
    swLong: number,
    zoom: number
};

export class Builder {
    ROOT: Root = ROOT_PATH;
    PATHS: string = '';
    COLON_PARAMETERS: Map<string, *> = new Map();
    SLASH_PARAMETERS: Map<string, *> = new Map();
    QUERY_PARAMETERS: Map<string, *> = new Map();
    BOUNDING_BOX_PARAMETERS: ?BoundingBoxParameters = null;

    addColonParameter(key: string, value: ?*): Builder {
        if (value || typeof value === 'number' || typeof value === 'boolean') {
            this.COLON_PARAMETERS.set(key, value);
        }

        return this;
    }

    buildFilterForUrl(filter: string) {
        // split <fieldName>:<refinementLink> into parts, defaulting to
        // filter:<filter> if fieldName is not specified
        const tokens = filter.split(':');
        const fieldName = encodeFilterValue(
            tokens.length > 1 ? tokens[0] : FILTER
        );
        const value = encodeFilterValue(tokens.length > 1 ? tokens[1] : filter);
        return {fieldName, value};
    }

    // group filters by their field name (legacy) or under 'filter' for consolidated
    // filters
    groupFilters(filters: ?(string[]) | ?string, map: Map<string, *>) {
        []
            .concat(filters || [])
            .sort()
            .forEach((f) => {
                const {fieldName, value} = this.buildFilterForUrl(f);

                // group the filters by fieldName
                const filtersForGroup = [].concat(map.get(fieldName) || []);
                filtersForGroup.push(value);
                map.set(fieldName, filtersForGroup);
            });
    }

    addFilterQueryParams(filters: ?(string[]) | ?string): Builder {
        this.groupFilters(filters, this.QUERY_PARAMETERS);
        return this;
    }

    addFilterPathSegments(filters: ?(string[]) | ?string): Builder {
        this.groupFilters(filters, this.COLON_PARAMETERS);
        return this;
    }

    addSlashParameter(key: string, value: ?*): Builder {
        if (value || typeof value === 'number' || typeof value === 'boolean') {
            this.SLASH_PARAMETERS.set(key, value);
        }

        return this;
    }

    addQueryParameter(key: string, value: ?*): Builder {
        if (value || typeof value === 'number' || typeof value === 'boolean') {
            this.QUERY_PARAMETERS.set(key, this.cleanedValues(key, value));
        }

        return this;
    }

    addQueryParameters(values: Object): Builder {
        Object.keys(values).forEach((key) =>
            this.addQueryParameter(key, values[key])
        );
        return this;
    }

    addBedroomParameters(
        minKey: string,
        maxKey: string,
        minValue: ?*,
        maxValue: ?*
    ): Builder {
        // exact studio match
        if (typeof minValue === 'number') {
            this.SLASH_PARAMETERS.set(minKey, minValue);
        }

        if (typeof maxValue === 'number') {
            this.SLASH_PARAMETERS.set(maxKey, maxValue);
        }

        return this;
    }

    addMapBoundingBoxParameters(
        boundingBoxParameters?: BoundingBoxParameters
    ): Builder {
        this.BOUNDING_BOX_PARAMETERS = boundingBoxParameters;
        return this;
    }

    cleanedValue(key: string, value: string): string {
        // filter ids can have 'filter:id' in them due to historical reasons, remove the extraneous 'filter'
        return key.toLowerCase() === FILTER ? value.split(':').pop() : value;
    }

    cleanedValues(key: string, value: string | string[]): string | string[] {
        if (Array.isArray(value)) {
            return value.map((v) => this.cleanedValue(key, v));
        }
        return this.cleanedValue(key, value);
    }

    colonPathSegment(key: string, value: string): string {
        return `${key}:${this.cleanedValue(key, value)}`;
    }

    build(): string {
        let url = [this.ROOT, this.PATHS];

        this.COLON_PARAMETERS.forEach(
            (value: string | string[], key: string) => {
                if (Array.isArray(value) && value.length > 0) {
                    url.push('/');
                    url.push(
                        value
                            .map((v) => this.colonPathSegment(key, v))
                            .join('/')
                    );
                } else if (!Array.isArray(value)) {
                    url.push('/');
                    url.push(this.colonPathSegment(key, value));
                }
            }
        );

        if (this.BOUNDING_BOX_PARAMETERS) {
            const {
                neLat,
                neLong,
                swLat,
                swLong,
                zoom
            } = this.BOUNDING_BOX_PARAMETERS;
            url.push(`/@${swLat},${swLong},${neLat},${neLong},${zoom}z`);
        }

        this.SLASH_PARAMETERS.forEach((value: string, key: string) => {
            url.push('/');
            url.push(`${key}/${value}`);
        });

        if (this.QUERY_PARAMETERS.size > 0) {
            let queryParams = [];

            this.QUERY_PARAMETERS.forEach(
                (value: string | string[], key: string) => {
                    const stringValue = value.toString();
                    const arrayParam =
                        (stringValue && stringValue.split(',')) || [];
                    arrayParam.forEach((arrayValue) => {
                        queryParams.push(`${key}=${arrayValue}`);
                    });
                }
            );

            url.push(`?${queryParams.join('&')}`);
        }

        return url.join('');
    }
}

export default class SearchUrlBuilder {
    constructor() {
        throw new Error('You can not construct builder');
    }

    static builder(): Builder {
        return new Builder();
    }
}
